import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  CircularProgress,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';
import walletApi from '../../api/wallet';
import { formatCurrency } from '../../utils/Currency';
import { CHART_COLORS } from '../../constants/layout';

const PERIOD_CONFIG = {
  '1w': { days: 7, view: 'daily', label: '1S' },
  '1m': { days: 30, view: 'daily', label: '1M' },
  '3m': { days: 90, view: 'monthly', label: '3M' },
  '6m': { days: 180, view: 'monthly', label: '6M' },
  '1y': { days: 365, view: 'monthly', label: '1A' },
  '2y': { days: 730, view: 'monthly', label: '2A' },
  'max': { days: 3650, view: 'yearly', label: 'MAX' },
};

const METRIC_CONFIG = {
  CurrentValue: { label: 'Valor Atual', key: 'CurrentValue' },
  TotalInvested: { label: 'Investido', key: 'TotalInvested' },
  ProfitLoss: { label: 'Lucro/Prejuízo', key: 'ProfitLoss' },
};

const CLASS_LABELS = {
  'FIXED_INCOME': 'Renda Fixa',
  'VARIABLE_INCOME': 'Renda Variável',
  'CRYPTO': 'Cripto',
  'CASH': 'Caixa',
  'OTHER': 'Outro',
  'Total': 'Total',
};

const getClassLabel = (className) => CLASS_LABELS[className] || className;

const InstrumentEvolutionChart = ({ currencyView }) => {
  const theme = useTheme();
  const [chartData, setChartData] = useState([]);
  const [assetClasses, setAssetClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const isMobile = window.innerWidth < 768;
    return isMobile ? '1m' : '3m';
  });
  const [selectedMetric, setSelectedMetric] = useState('CurrentValue');

  const periodConfig = useMemo(() => PERIOD_CONFIG[selectedPeriod], [selectedPeriod]);

  const formatDateKey = (date, view) => {
    switch (view) {
      case 'monthly':
        return `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
      case 'yearly':
        return `${date.getFullYear()}`;
      default:
        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
    }
  };

  const transformData = (positions, metric, currency, view) => {
    const dateMap = new Map();
    const classSet = new Set();

    positions.forEach((pos) => {
      if (!pos.PositionDate) return;

      const assetName = pos.Asset?.Name;
      if (!assetName || assetName === 'Total') return;

      const date = new Date(pos.PositionDate);
      const dateKey = formatDateKey(date, view);

      classSet.add(assetName);

      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, { date: dateKey, _sortDate: date });
      }

      const dataPoint = dateMap.get(dateKey);
      const value = parseFloat(pos[metric]?.[currency] || 0);
      dataPoint[assetName] = value;
    });

    const classes = [...classSet].sort();

    const sortedData = [...dateMap.values()]
      .sort((a, b) => a._sortDate - b._sortDate)
      .map(({ _sortDate, ...rest }) => {
        const point = { ...rest };
        classes.forEach(cls => {
          if (point[cls] === undefined) {
            point[cls] = null;
          }
        });
        return point;
      });

    return {
      data: sortedData,
      classes,
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');

      try {
        const now = new Date();
        const startDate = new Date();
        startDate.setDate(now.getDate() - periodConfig.days);

        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = now.toISOString().split('T')[0];

        const url = `/positions/asset-class?StartDate=${formattedStartDate}&EndDate=${formattedEndDate}&View=${periodConfig.view}`;
        const response = await walletApi.get(url);

        if (response.status === 204 || !response.data) {
          setChartData([]);
          setAssetClasses([]);
          return;
        }

        const positions = response.data.Positions || [];

        if (positions.length === 0) {
          setChartData([]);
          setAssetClasses([]);
          return;
        }

        const { data, classes } = transformData(
          positions,
          selectedMetric,
          currencyView,
          periodConfig.view
        );

        setChartData(data);
        setAssetClasses(classes);
      } catch (err) {
        console.error('Error fetching evolution data:', err);
        setError('Erro ao carregar dados de evolução.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedPeriod, selectedMetric, currencyView, periodConfig]);

  const series = useMemo(() => {
    return assetClasses.map((assetClass, index) => ({
      dataKey: assetClass,
      label: getClassLabel(assetClass),
      color: CHART_COLORS[index % CHART_COLORS.length],
      connectNulls: true,
      valueFormatter: (value) => {
        if (value === null || value === undefined) return '-';
        return formatCurrency(currencyView, value);
      },
    }));
  }, [assetClasses, currencyView]);

  const yAxisConfig = useMemo(() => {
    if (chartData.length === 0) return { min: 0, max: 100 };

    const allValues = chartData.flatMap((d) =>
      assetClasses.map((cls) => d[cls]).filter(v => v !== null && v !== undefined)
    );

    if (allValues.length === 0) return { min: 0, max: 100 };

    const minVal = Math.min(...allValues);
    const maxVal = Math.max(...allValues);

    if (minVal === 0 && maxVal === 0) return { min: 0, max: 100 };

    const range = maxVal - minVal || maxVal * 0.1;
    return {
      min: selectedMetric === 'ProfitLoss' ? minVal - range * 0.1 : Math.max(0, minVal - range * 0.1),
      max: maxVal + range * 0.1,
    };
  }, [chartData, assetClasses, selectedMetric]);

  const calculateLeftMargin = () => {
    if (chartData.length === 0) return 70;
    const allValues = chartData.flatMap((d) =>
      assetClasses.map((cls) => d[cls]).filter(v => v !== null && v !== undefined)
    );
    if (allValues.length === 0) return 70;
    const maxValue = Math.max(...allValues.map(Math.abs));
    const digits = Math.floor(Math.log10(maxValue || 1)) + 1;
    return Math.max(70, digits * 10 + 40);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Evolução por Classe de Ativo ({currencyView})
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Período
          </Typography>
          <ToggleButtonGroup
            value={selectedPeriod}
            exclusive
            onChange={(_, value) => value && setSelectedPeriod(value)}
            size="small"
          >
            {Object.entries(PERIOD_CONFIG).map(([key, config]) => (
              <ToggleButton
                key={key}
                value={key}
                sx={{
                  px: 1.5,
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                  },
                }}
              >
                {config.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Métrica
          </Typography>
          <ToggleButtonGroup
            value={selectedMetric}
            exclusive
            onChange={(_, value) => value && setSelectedMetric(value)}
            size="small"
          >
            {Object.entries(METRIC_CONFIG).map(([key, config]) => (
              <ToggleButton
                key={key}
                value={key}
                sx={{
                  px: 1.5,
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                  },
                }}
              >
                {config.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box
        sx={{
          height: 400,
          width: '100%',
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {loading && <CircularProgress />}
        {error && (
          <Typography color="error">{error}</Typography>
        )}
        {!loading && !error && chartData.length === 0 && (
          <Typography color="text.secondary">
            Nenhum dado histórico disponível para o período selecionado.
          </Typography>
        )}
        {!loading && !error && chartData.length > 0 && (
          <LineChart
            xAxis={[
              {
                dataKey: 'date',
                scaleType: 'point',
              },
            ]}
            yAxis={[
              {
                min: yAxisConfig.min,
                max: yAxisConfig.max,
                tickNumber: 6,
              },
            ]}
            series={series}
            dataset={chartData}
            height={360}
            grid={{ horizontal: true }}
            margin={{
              left: calculateLeftMargin(),
              right: 20,
              top: 40,
              bottom: 30,
            }}
            slotProps={{
              legend: {
                direction: 'row',
                position: { vertical: 'top', horizontal: 'middle' },
                padding: 0,
              },
            }}
          />
        )}
      </Box>
    </Paper>
  );
};

export default InstrumentEvolutionChart;
